import { useApolloClient } from '@apollo/client';
import { chain } from 'lodash';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CareTypeNames } from '../../Components/CareTypeNames';
import { CampusTeamActivity } from '../../Components/CampusTeam/CampusTeamActivity';
import { FinalButton } from '../../Components/FinalButton';
import { AsyncListView } from '../../Components/ListView/AsyncListView';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { UniversityLogo } from '../../Components/Organization/UniversityLogo';
import { PaddedPage, Text } from '../../globalStyles';
import { compareLastNames, getFullNameReversed } from '../../modelUtils/users';
import { taskStatusOptions } from '../UserList/Shared';
import { ReferrerList } from './ReferrerList';
import {
  PermsOnly,
  isMantraProvider,
  isUniversityReferrer,
  isUniversityStaff,
  isSupportingClinician,
  useCurrentProvider,
} from '../../Components/Permissions';
import {
  Entitlement,
  Permission,
  Role,
  SortDirection,
  StudentsDocument,
  StudentsQuery,
  StudentsQueryVariables,
  useStudentListOptionsQuery,
} from '../../graphQL';

export function StudentsPage() {
  const { hasPermission } = useCurrentProvider();

  if (hasPermission(Permission.PatientList)) return <DefaultView />;
  return <ReferrerList />;
}

function DefaultView() {
  const history = useHistory();
  const params = useParams() as Record<string, string>;
  const { currentProvider } = useCurrentProvider();
  const organizationId = Number(params.organizationId);
  const { data: optData } = useStudentListOptionsQuery({ variables: { organizationId } });
  const apolloClient = useApolloClient();

  const loadData = useCallback(
    async (uiState, prevPage) => {
      // Always filter student list for university referrers, otherwise only filter student list
      // for uni staff/clinician and oz provider when their search value is blank
      const filterStudentList =
        isUniversityReferrer(currentProvider) ||
        ((isUniversityStaff(currentProvider) ||
          isSupportingClinician(currentProvider) ||
          isMantraProvider(currentProvider)) &&
          uiState.debouncedSearchValue === '');
      const defaultCareTeamMemberId = filterStudentList ? currentProvider.id : null;

      const { data } = await apolloClient.query<StudentsQuery, StudentsQueryVariables>({
        query: StudentsDocument,
        variables: {
          params: {
            dtc: false,
            cursor: prevPage?.cursor,
            search: uiState.debouncedSearchValue,
            organizationId: uiState.filterValues?.organizationId ?? organizationId,
            careTeamMemberId: defaultCareTeamMemberId ?? uiState.filterValues?.campusTeam,
            taskStatus: uiState.filterValues?.taskStatus,
            sortField: uiState.sortColumn,
            sortDirection:
              uiState.sortDirection === 'desc' ? SortDirection.Descending : SortDirection.Ascending,
            limit: uiState.limit,
            includeAllPatients: true,
          },
        },
      });
      if (!data) {
        throw new Error('Could not find student data');
      }
      return data.paginatedUsers;
    },
    [apolloClient, currentProvider]
  );

  if (!optData) {
    return <LoadingPage />;
  }

  const isSuperOrg = optData.organization.children.length > 0;
  const isUniSupportingClinician = currentProvider.role === Role.UniversitySupportingClinician;

  const showIdentification = optData.organization.entitlements.some(
    i => i.key === Entitlement.OrgIdentification
  );

  return (
    <StudentsContainerGrid>
      <PaddedPage className="relative">
        <UniversityLogo />
        <div className="flex mb4">
          <Text.h1>Students</Text.h1>
          <PermsOnly allowed="patientCreate">
            <div className="ml3">
              <FinalButton
                pill
                iconLeft="iconsWhitePlusSvg"
                onClick={() => history.push(`/organizations/${organizationId}/students/enroll`)}
                data-cy="addStudentButton"
              >
                Add
              </FinalButton>
            </div>
          </PermsOnly>
        </div>
        <AsyncListView
          fetchData={loadData}
          hideTopControls
          analyticsName="students-page-list"
          pageSize={10}
          initialSortColumn="name"
          initialSortDirection="asc"
          search={(user, query) => {
            const q = query.toLowerCase();
            return (
              user.firstName.toLowerCase().includes(q) || user.lastName.toLowerCase().includes(q)
            );
          }}
          columns={[
            {
              key: 'name',
              title: 'Name',
              render: user => (
                <div>
                  <Text.bodyBold data-cy="name" className="ttc">
                    {getFullNameReversed(user)}
                  </Text.bodyBold>
                  {showIdentification && (
                    <Text.captionGrey>{user.organizationIdentification ?? 'n/a'}</Text.captionGrey>
                  )}
                  {!showIdentification && (
                    <Text.captionGrey>
                      <CareTypeNames careTypes={user.careTypes} />
                    </Text.captionGrey>
                  )}
                </div>
              ),
              sort: compareLastNames,
            },
            {
              key: 'campusTeam',
              title: 'Campus Team',
              render: user =>
                user.campusTeam && user.campusTeam.length
                  ? user.campusTeam.map(c => c.provider.name).join(', ')
                  : 'None',
            },
            {
              key: 'taskStatus',
              title: 'Mantra Status',
              render: user =>
                user.taskStatus || (
                  <Text.body kind="grayText" className="i">
                    hidden
                  </Text.body>
                ),
              sort: (a, b) => (a.taskStatus ?? '').localeCompare(b.taskStatus ?? ''),
            },
          ]}
          getKey={user => user.id}
          link={user => `/users/${user.id}`}
          filters={[
            {
              key: 'organizationId',
              placeholder: 'Campus',
              options: optData.organization.children
                .map(i => ({ label: i.name, id: i.id }))
                .sort((a, b) => a.label.localeCompare(b.label)),
              hidden: !isSuperOrg,
            },
            {
              key: 'campusTeam',
              placeholder: 'Campus Team',
              options: chain(optData.organizationCampusTeam ?? [])
                .map(({ provider }) => provider)
                .uniqBy(provider => provider.id)
                .map(provider => ({
                  id: provider.id,
                  label: provider.name,
                }))
                .value(),
              hidden: isUniSupportingClinician,
            },
            {
              key: 'taskStatus',
              placeholder: 'Mantra Status',
              options: taskStatusOptions,
            },
          ]}
        />
      </PaddedPage>
      <CampusTeamActivityContainer>
        <CampusTeamActivity />
      </CampusTeamActivityContainer>
    </StudentsContainerGrid>
  );
}

const StudentsContainerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 30rem;

  @media only screen and (max-width: 60rem) {
    grid-template-columns: 1fr;
    & > div:first-of-type {
      margin-bottom: 2rem;
    }
  }
`;

const CampusTeamActivityContainer = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  height: 100%;
`;
